<template>
  <a
    :href="recentOpportunity.track_link"
    target="_blank"
    rel="noopener noreferrer"
    class="card tw-w-full tw-rounded-xs tw-bg-gray-100 tw-px-4 tw-py-3 tw-shadow-round md:tw-transition-all md:tw-duration-100 md:tw-ease-linear"
    @click="handleCardClick"
  >
    <div class="tw-flex tw-items-center">
      <div class="tw-h-10 tw-w-10 tw-rounded-xs">
        <img
          v-if="recentOpportunity.track_album_cover"
          class="tw-h-full tw-w-full tw-object-cover"
          :src="recentOpportunity.track_album_cover"
        />
        <div
          v-else
          class="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-orange-200"
        >
          <i class="fas icon fa-music tw-text-orange-500" />
        </div>
      </div>
      <div class="tw-ml-3 tw-flex tw-w-[80%] tw-flex-col">
        <VText
          cfg="sans/16/medium"
          class="tw-mb-1 tw-w-full tw-overflow-hidden tw-truncate"
        >
          {{ recentOpportunity.track_name }}
        </VText>
        <VText cfg="sans/14/regular" color="gray-500">
          {{ recentOpportunity.artist_name }}
        </VText>
      </div>
    </div>
    <div class="tw-my-3 tw-h-[1px] tw-w-full tw-bg-gray-300"></div>
    <div class="tw-flex tw-items-center">
      <img
        :src="influencerPicture"
        class="tw-h-6 tw-w-6 tw-rounded-full tw-object-cover tw-shadow-round"
        alt=""
      />
      <span class="tw-ml-[6px] tw-mr-1">{{ shareDecision }}</span>
      <i :class="shareIcon"></i>
    </div>
  </a>
</template>

<script lang="ts">
import { mapStores } from 'pinia'
import { defineComponent } from 'vue'

import VText from '~/components/ui/VText.vue'

import SegmentMixin from '~/mixins/Segment/Segment'

import { useUserStore } from '~/stores/user'

import pictureBuildHelper from '~/helpers/images/picture_build'

import type {
  Influencer,
  InfluencerRecentOpportunities,
} from '~/types/influencer'
import type { PropType } from 'vue'

export default defineComponent({
  components: {
    VText,
  },
  mixins: [SegmentMixin],
  props: {
    recentOpportunity: {
      type: Object as PropType<InfluencerRecentOpportunities>,
      required: true,
    },
    influencer: {
      type: Object as PropType<Influencer>,
      required: true,
    },
  },
  computed: {
    ...mapStores(useUserStore),
    USER() {
      return this.userStore.$state
    },
    shareDecision(): string {
      return this.$t(
        `influencer.profile.slug.recentOpportunities.shareDecision.${this.recentOpportunity.decision_name}`,
      ) as string
    },
    shareIcon(): string {
      switch (this.recentOpportunity.decision_name) {
        case 'article':
          return 'fa-solid fa-pen-nib'
        case 'channel-upload':
          return 'fa-solid fa-tv'
        case 'interview':
          return 'fa-solid fa-microphone-lines'
        case 'live-events':
          return 'fa-solid fa-calendar-days'
        case 'meeting':
          return ' fa-regular fa-handshake'
        case 'playlist':
          return 'fa-solid fa-headphones-simple'
        case 'radio-broadcast':
          return 'fa-solid fa-radio'
        case 'social-media-sharing':
          return 'fa-regular fa-thumbs-up'
        case 'spotify-playlist':
          return 'fa-solid fa-headphones-simple'
        case 'youtube-channel':
          return 'fa-solid fa-tv'
        default:
          return 'fa-solid fa-pen-nib'
      }
    },
    influencerPicture(): string {
      return pictureBuildHelper({
        target: 'profile_picture',
        size: '40_40',
        slug: this.influencer.has_profile_picture
          ? this.influencer.slug
          : undefined,
        kind: 'influencer',
      })
    },
  },
  methods: {
    handleCardClick() {
      this.trackSegmentEvent('Curator Profile - Recent Share Clicked', {
        id_curator: this.influencer.id,
        id_user: this.USER.id,
        share_type: this.recentOpportunity.decision_name,
      })
    },
  },
})
</script>

<style scoped>
.card:hover {
  box-shadow: var(--shadow-regular-dark);
}
</style>
